/**
 * Offline rendering + a time-sliced bake queue.
 *
 * Anything with a fixed character is rendered once through an OfflineAudioContext
 * and played back as a buffer, so a firefight costs one BufferSource per shot
 * instead of twenty live nodes. The queue yields to the event loop between jobs so
 * boot never stalls.
 */
import { normaliseBuffer } from './nodes.js';

export const DEFAULT_SR = 48000;

export function hasOffline() {
  return typeof OfflineAudioContext !== 'undefined';
}

/**
 * Render `build(ac, destination)` into an AudioBuffer.
 * @param {number} seconds
 * @param {(ac: OfflineAudioContext, dest: AudioNode) => void} build
 * @returns {Promise<AudioBuffer|null>} null if Web Audio is unavailable.
 */
export async function renderOffline(seconds, build, { sampleRate = DEFAULT_SR, channels = 1, normalise = true, target = 0.985 } = {}) {
  if (!hasOffline()) return null;
  const frames = Math.max(128, Math.ceil(seconds * sampleRate));
  let ac;
  try {
    ac = new OfflineAudioContext(channels, frames, sampleRate);
  } catch {
    return null;
  }
  try {
    build(ac, ac.destination);
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[audio] bake build failed', err);
    return null;
  }
  let buf;
  try {
    buf = await ac.startRendering();
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[audio] bake render failed', err);
    return null;
  }
  if (normalise) normaliseBuffer(buf, target);
  return buf;
}

const nextTick = (fn) => {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => fn(), { timeout: 60 });
  else setTimeout(fn, 0);
};

/**
 * Serial bake queue. Jobs run one at a time with a yield in between so the main
 * thread stays responsive. Errors are swallowed into a null result — a failed bake
 * degrades to the live-graph fallback, it never breaks the frame.
 */
export class Baker {
  constructor() {
    this.queue = [];
    this.running = false;
    this.done = 0;
    this.failed = 0;
    this._idle = [];
  }

  /** @returns {Promise<AudioBuffer|null>} */
  enqueue(label, fn) {
    return new Promise((resolve) => {
      this.queue.push({ label, fn, resolve });
      this._pump();
    });
  }

  get pending() {
    return this.queue.length + (this.running ? 1 : 0);
  }

  /** Resolves when the queue has fully drained (or immediately if it is empty). */
  idle() {
    if (this.pending === 0) return Promise.resolve();
    return new Promise((r) => this._idle.push(r));
  }

  _pump() {
    if (this.running || this.queue.length === 0) return;
    this.running = true;
    const job = this.queue.shift();
    Promise.resolve()
      .then(() => job.fn())
      .then(
        (v) => { this.done++; job.resolve(v); },
        (err) => {
          this.failed++;
          if (typeof console !== 'undefined') console.warn(`[audio] bake "${job.label}" failed`, err);
          job.resolve(null);
        },
      )
      .then(() => {
        this.running = false;
        if (this.queue.length) nextTick(() => this._pump());
        else { const w = this._idle; this._idle = []; for (const r of w) r(); }
      });
  }
}
